import { Building2, CameraOff, LucideIcon, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ReactNode } from "react";

export interface NoFoundProps {
    title?: string;
    description?: string;
    hint?: string;
    actionText?: string;
    onAction?: () => void;
    showAction?: boolean;
    showHint?: boolean;
    icon?: LucideIcon;
    badgeIcon?: LucideIcon;
    titleTranslationKey?: string;
    descriptionTranslationKey?: string;
    hintTranslationKey?: string;
    actionTranslationKey?: string;
    children?: ReactNode;
}

const NoFound = ({
    title,
    description,
    hint,
    actionText,
    onAction,
    showAction = true,
    showHint = true,
    icon: Icon = Building2,
    badgeIcon: BadgeIcon = CameraOff,
    titleTranslationKey = "common.noData.title",
    descriptionTranslationKey = "common.noData.description",
    hintTranslationKey = "common.noData.hint",
    actionTranslationKey = "common.actions.add",
    children,
}: NoFoundProps) => {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col items-center justify-center h-full space-y-5 py-8">
            <div className="relative">
                <Icon className="w-16 h-16 text-muted-foreground/30" />
                <div className="absolute -top-2 -right-2 rounded-full bg-muted p-1">
                    <BadgeIcon className="w-5 h-5 text-muted-foreground" />
                </div>
            </div>
            <div className="text-center space-y-2">
                <h3 className="text-lg font-medium text-foreground">
                    {title || t(titleTranslationKey)}
                </h3>
                <p className="text-muted-foreground max-w-md">
                    {description || t(descriptionTranslationKey)}
                </p>
            </div>

            {showAction && onAction && (
                <Button
                    onClick={onAction}
                    className="mt-2 animate-pulse"
                    size="lg"
                >
                    <Plus className="mr-2 h-4 w-4" />
                    {actionText || t(actionTranslationKey)}
                </Button>
            )}

            {children}

            {showHint && (
                <div className="text-xs text-muted-foreground/70 mt-4">
                    {hint || t(hintTranslationKey)}
                </div>
            )}
        </div>
    );
};

export default NoFound;